import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DriverWorkflow, UserRole } from '@symfa-inc/providence-verizon-types';
import { Spin } from 'antd';
import { HttpService } from '@core/services';
import {
  DriverManagementHttpService,
  FinancialHttpService,
} from '@core/services/http';
import { getProjectBundleIDParamFromUrl } from '@core/utils/methods';
import { Financial } from '@shared/components/financial';
import { FinancialProps } from '@shared/components/financial/models';
import { EngineeringActions, FinancialActions } from '@store/actions';
import { EngineeringSelectors, UserSelectors } from '@store/selectors';

type EngineeringFinancialTabProps = FinancialProps<any> & {
  isActive?: boolean;
};

export const EngineeringFinancialTab: FC<EngineeringFinancialTabProps> = ({
  isActive,
  ...financialProps
}) => {
  const dispatch = useDispatch();
  const [projectBundleID] = getProjectBundleIDParamFromUrl();
  const isLoading = useSelector(
    EngineeringSelectors.getTabLoadingState('financial'),
  );
  const isDataLoaded = useSelector(
    EngineeringSelectors.getTabLoadedState('financial'),
  );
  const roles = useSelector(UserSelectors.getUserRoles);

  const readOnly = roles.length === 1 && roles[0] === UserRole.ReadOnly;

  useEffect(() => {
    if (isActive && !isDataLoaded && !isLoading) {
      dispatch(
        FinancialActions.getFinancialDataAction.done({
          id: '',
          siteFees: [],
          servicesRequired: [],
        }),
      );

      if (readOnly) {
        dispatch(
          EngineeringActions.setTabLoaded.done({
            tabName: 'financial',
            isLoaded: true,
          }),
        );

        return;
      }

      dispatch(
        EngineeringActions.setTabLoading.done({
          tabName: 'financial',
          isLoading: true,
        }),
      );

      const loadFinancialTab = async (): Promise<void> => {
        try {
          await HttpService.getHttpRequests(FinancialHttpService).getFinancial(
            projectBundleID,
            'engineering',
          );
          await HttpService.getHttpRequests(
            DriverManagementHttpService,
          ).getDriversListByWorkflow(DriverWorkflow.Engineering);
          dispatch(
            EngineeringActions.setTabLoaded.done({
              tabName: 'financial',
              isLoaded: true,
            }),
          );
        } catch {
          dispatch(
            EngineeringActions.setTabError.done({
              tabName: 'financial',
              hasError: true,
            }),
          );
        }
      };

      loadFinancialTab();
    }
  }, [dispatch, isActive, isDataLoaded, isLoading, projectBundleID, readOnly]);

  if (isLoading || !isDataLoaded) {
    return <Spin size="large" />;
  }

  return <Financial {...financialProps} />;
};
